import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Surface } from '../../../ui/components';
import {
  ArrowLeft, ArrowRight, Upload, CheckCircle, FileText, ShieldCheck,
} from 'lucide-react';
import { addDocument } from '../../../store/loanApplicationSlice';
import { LK_FEATURES } from '../../../config/features';
import { getV1Product } from '../../../config/v1/products';
import {
  buildV1DocumentContext,
  getVisibleV1Documents,
  getV1DocCompletenessPercent,
  V1_DEFERRED_TO_LENDER,
} from '../../../config/v1/documents';
import { V1_DISCLAIMERS, V1_LABELS } from '../../../config/v1/complianceCopy';
import { getV1FieldCompletenessPercent } from '../../../config/v1/fields';
import ApplyLayout from '../../layout/ApplyLayout';

const V1DocumentPrep = ({ productId, triageAnswers, fieldAnswers, onBack, onContinue }) => {
  const dispatch = useDispatch();
  const documents = useSelector((s) => s.loanApplication.documents) || [];
  const product = getV1Product(productId);

  const ctx = useMemo(
    () => buildV1DocumentContext(triageAnswers, fieldAnswers),
    [triageAnswers, fieldAnswers],
  );

  const docList = useMemo(
    () => getVisibleV1Documents(productId, ctx),
    [productId, ctx],
  );

  const [uploadedMap, setUploadedMap] = useState({});
  const fileInputRef = useRef(null);
  const [pendingDocId, setPendingDocId] = useState(null);

  useEffect(() => {
    const next = {};
    documents.forEach((d) => {
      if (d?.docId) next[d.docId] = true;
      else if (d?.docName) next[d.docName] = true;
    });
    setUploadedMap(next);
  }, [documents]);

  const docCompleteness = getV1DocCompletenessPercent(productId, ctx, uploadedMap);
  const fieldCompleteness = getV1FieldCompletenessPercent(productId, fieldAnswers);

  const handleUploadClick = (doc) => {
    setPendingDocId(doc.id);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingDocId) return;
    const doc = docList.find((d) => d.id === pendingDocId);
    dispatch(addDocument({
      docId: pendingDocId,
      docName: doc?.label || pendingDocId,
      fileName: file.name,
      fileSize: file.size,
      uploadedAt: new Date().toISOString(),
    }));
    setUploadedMap((prev) => ({ ...prev, [pendingDocId]: true }));
    setPendingDocId(null);
    e.target.value = '';
  };

  const requiredDocs = docList.filter((d) => d.required !== false);
  const uploadedRequired = requiredDocs.filter((d) => uploadedMap[d.id]).length;

  return (
    <ApplyLayout
      title="Documents for your packet"
      subtitle="Upload what you have now — lenders may request additional items later."
      contentMaxWidth="full"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.heic,.webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Surface
        padding="var(--lk-space-4)"
        style={{
          marginBottom: 'var(--lk-space-5)',
          backgroundColor: 'var(--lk-primary-50)',
          border: '1px solid var(--lk-primary-200)',
        }}
      >
        <strong>{product?.name}</strong>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--lk-primary-700)' }}>
              {V1_LABELS.fileCompleteness} (fields)
            </div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{fieldCompleteness}%</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--lk-primary-700)' }}>Documents uploaded</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              {uploadedRequired}/{requiredDocs.length}
            </div>
            <div style={{ fontSize: 13, color: 'var(--lk-text-secondary)' }}>{docCompleteness}% complete</div>
          </div>
        </div>
      </Surface>

      <p style={{ fontSize: 13, color: 'var(--lk-text-tertiary)', marginBottom: 20 }}>
        {V1_DISCLAIMERS.global}
      </p>

      <div>
        {docList.map((doc) => {
          const done = !!uploadedMap[doc.id];
          return (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                padding: '16px 18px',
                marginBottom: 12,
                borderRadius: 'var(--lk-radius-lg)',
                border: done ? '2px solid var(--lk-success-400)' : '1px solid var(--lk-border)',
                backgroundColor: done ? 'var(--lk-success-50)' : 'white',
              }}
            >
              <div style={{ marginTop: 2, color: done ? 'var(--lk-success-600)' : 'var(--lk-text-tertiary)' }}>
                {done ? <CheckCircle size={22} /> : <FileText size={22} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{doc.label}</span>
                  {doc.required !== false ? (
                    <span style={{
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      padding: '2px 8px', borderRadius: 4,
                      backgroundColor: 'var(--lk-danger-50)', color: 'var(--lk-danger-600)',
                    }}>
                      Required
                    </span>
                  ) : (
                    <span style={{
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                      color: 'var(--lk-text-tertiary)',
                    }}>
                      Optional
                    </span>
                  )}
                </div>
                {doc.hint && (
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--lk-text-tertiary)' }}>
                    {doc.hint}
                  </p>
                )}
              </div>
              {!done && (
                <Button variant="outline" size="sm" icon={Upload} onClick={() => handleUploadClick(doc)}>
                  Upload
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {V1_DEFERRED_TO_LENDER.length > 0 && (
        <details style={{ marginTop: 8, marginBottom: 24 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            Items your lender may collect later
          </summary>
          <ul style={{ margin: '12px 0 0', paddingLeft: 20, color: 'var(--lk-text-secondary)', fontSize: 14 }}>
            {V1_DEFERRED_TO_LENDER.map((item) => (
              <li key={item} style={{ marginBottom: 6 }}>{item}</li>
            ))}
          </ul>
        </details>
      )}

      {LK_FEATURES.digitalVerification && (
        <Surface
          padding="var(--lk-space-4)"
          style={{
            marginBottom: 24,
            border: '1px dashed var(--lk-border)',
            backgroundColor: 'var(--lk-bg-surface-2)',
          }}
        >
          <span style={{ fontWeight: 600 }}>Digital verification</span>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--lk-text-secondary)' }}>
            Connect accounts to verify income and assets (when enabled).
          </p>
        </Surface>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24,
        fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)',
      }}>
        <ShieldCheck size={14} />
        <span>Documents are only shared with lenders you select.</span>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <Button variant="outline" size="lg" icon={ArrowLeft} onClick={onBack}>
          Back
        </Button>
        <Button variant="primary" size="lg" icon={ArrowRight} onClick={onContinue} style={{ flex: 1 }}>
          {uploadedRequired > 0 ? 'Continue' : 'Skip for now & continue'}
        </Button>
      </div>
    </ApplyLayout>
  );
};

export default V1DocumentPrep;

